import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '../store/editorStore';
import { ASPECT_PRESETS } from '../constants';

/** Small aspect-ratio icon preview */
function RatioIcon({ ratio, active }: { ratio: number; active: boolean }) {
  // Draw a small rectangle showing the ratio shape
  const maxW = 24;
  const maxH = 20;
  let w: number, h: number;

  if (ratio <= 0) {
    // "Original" — dashed border
    return (
      <div
        className="border border-dashed rounded-sm"
        style={{
          width: maxW,
          height: maxH,
          borderColor: active ? 'var(--user-page-accent)' : 'currentColor',
          opacity: active ? 1 : 0.4,
        }}
      />
    );
  }

  if (ratio >= 1) {
    w = maxW;
    h = Math.round(maxW / ratio);
  } else {
    h = maxH;
    w = Math.round(maxH * ratio);
  }

  return (
    <div
      className="rounded-sm"
      style={{
        width: w,
        height: h,
        backgroundColor: active ? 'var(--user-page-accent)' : 'currentColor',
        opacity: active ? 1 : 0.3,
      }}
    />
  );
}

export function AspectRatioSelector() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const projectAspect = useEditorStore((s) => s.project.projectAspect);
  const setProjectAspect = useEditorStore((s) => s.setProjectAspect);

  const activePreset = ASPECT_PRESETS.find((p) => Math.abs(p.value - projectAspect) < 0.01)
    ?? ASPECT_PRESETS[1]; // fallback to 16:9

  function handleSelect(value: number) {
    // Store update triggers bridge subscription → Three.js camera update
    setProjectAspect(value || 16 / 9);
    setOpen(false);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-[var(--radius-chip)] bg-[var(--user-page-card-bg)] border border-[var(--user-page-card-border)] hover:border-[var(--user-page-accent)] transition-colors text-xs"
        title={t('editor.aspectRatio', 'Aspect Ratio')}
      >
        <RatioIcon ratio={activePreset.value} active />
        <span className="font-medium text-[var(--user-page-text)]">{activePreset.label}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full mt-1 start-0 z-50 w-52 py-1 rounded-[var(--radius-inner)] bg-[var(--user-page-card-bg)] border border-[var(--user-page-card-border)] shadow-xl">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--user-page-text)] opacity-40">
            {t('editor.canvasSize', 'Canvas Size')}
          </div>
          {ASPECT_PRESETS.map((preset) => {
            const isActive = Math.abs(preset.value - projectAspect) < 0.01;
            return (
              <button
                key={preset.label}
                onClick={() => handleSelect(preset.value)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-start transition-colors
                  ${isActive
                    ? 'bg-[var(--user-page-accent)]/10 text-[var(--user-page-accent)]'
                    : 'text-[var(--user-page-text)] hover:bg-[var(--user-nav-hover-bg)]'
                  }
                `}
              >
                <div className="flex items-center justify-center w-7 h-5">
                  <RatioIcon ratio={preset.value} active={isActive} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{preset.label}</div>
                  {preset.subtitle && (
                    <div className="text-[10px] opacity-50">{preset.subtitle}</div>
                  )}
                </div>
                {isActive && (
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
